import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { FC } from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { stringifyError } from '../../../utils/utils-functions'
import { enqueueSnackbar } from 'notistack'
import { createUser } from '../../../services/permissions/api'
import { useForm } from 'react-hook-form'
import { CreateUserData } from '../../../services/permissions/types'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ControlledTextField from '../../controlledInputs/ControlledTextField'
import ControlledCheckbox from '../../controlledInputs/ControlledCheckbox'

export type CreateUserProps = {
  onClose: (value: boolean) => void;
  open: boolean;
}

const schema = yup.object().shape({
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  email: yup.string().email('Invalid email').required('Required'),
  password: yup.string().required('Required'),
  is_read_only: yup.boolean().required('Required')
})

const CreateUserDialog: FC<CreateUserProps> = ({ onClose, open }) => {
  const queryClient = useQueryClient()

  const { mutateAsync: crateUser, isLoading: createUserLoading } = useMutation(createUser)

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      is_read_only: false
    },
    mode: 'onBlur',
    resolver: yupResolver(schema)
  })

  const handlerClose = () => {
    onClose(false)
  }

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      await crateUser(data)
      queryClient.refetchQueries({
        queryKey: ['permissionUsers']
      })
      enqueueSnackbar('User created', { variant: 'success' })
      onClose(false)
    } catch (e) {
      console.error(e)
      enqueueSnackbar(stringifyError(e), { variant: 'error' })
    }
  }

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={handlerClose}
    >
      <DialogTitle>
        Create User
      </DialogTitle>
      <DialogContent>
        <form
          id="create-user-form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(handleCreateUser)}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                name="first_name"
                control={control}
                error={errors.first_name}
                label="First Name"
                margin="normal"
                size="medium"/>
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                name="last_name"
                control={control}
                error={errors.last_name}
                label="Last Name"
                margin="normal"
                size="medium"/>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                name="email"
                control={control}
                error={errors.email}
                label="Email"
                size="medium"/>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                name="password"
                control={control}
                error={errors.password}
                label="Password"
                size="medium"/>
            </Grid>
            <Grid item xs={12}>
              <ControlledCheckbox
                name="is_read_only"
                control={control}
                label="Read Only"
                labelPlacement="end"
                size="medium"/>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handlerClose}>Close</Button>
        <LoadingButton
          form="create-user-form"
          loading={createUserLoading}
          type="submit"
          variant="contained">
          Create
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default CreateUserDialog
